import { api } from '@/api/client';
import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

export class AuthService {
  /**
   * Redirect to WorkOS login for member authentication
   */
  static login(): void {
    window.location.href = `${API_BASE_URL}/auth/member/login`;
  }

  /**
   * Logout current user - clear state and redirect to WorkOS logout
   */
  static logout(): void {
    useAuthStore.getState().logout();
    const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN || '.gmhsc.local';
    const cookiePath = import.meta.env.VITE_COOKIE_PATH || '/';
    const cookieSameSite = import.meta.env.VITE_COOKIE_SAME_SITE || 'lax';
    document.cookie = `workos_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${cookiePath}; domain=${cookieDomain}; secure; samesite=${cookieSameSite}`;

    setTimeout(() => {
      window.location.href = `${API_BASE_URL}/auth/member/logout`;
    }, 100);
  }

  /**
   * Get current user profile using WorkOS session cookie
   */
  static async getProfile(): Promise<User> {
    try {
      const userProfile = await api.get<User>('/member/me');

      return {
        id: userProfile.id,
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        email: userProfile.email,
        status: 'active',
        createdAt: userProfile.createdAt || new Date().toISOString(),
        updatedAt: userProfile.updatedAt || new Date().toISOString(),
      };
    } catch (error) {
      console.error('Failed to get user profile:', error);
      throw error;
    }
  }

  /**
   * Handle OAuth callback from WorkOS
   */
  static async handleCallback(): Promise<{ success: boolean; error?: string }> {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    try {
      await api.get(
        `/auth/member/callback?code=${encodeURIComponent(code ?? '')}&state=${encodeURIComponent(state ?? '')}`,
      );
      return { success: true };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Check if user is authenticated by attempting to get profile
   */
  static async checkAuth(): Promise<boolean> {
    try {
      await this.getProfile();
      return true;
    } catch {
      return false;
    }
  }
}
